import { createSelector } from 'reselect'
import {
  prop,
  curry,
  includes,
  flatten,
  compose,
  all,
  __,
  map,
  equals,
  complement,
  not,
  reduce,
  cond,
  concat,
  T,
  always,
  append,
} from 'ramda'

import { Set } from 'immutable'

import {
  safeToJS,
  getIn,
  getInOr,
  pickToJS,
  get,
  getEq,
  has,
  matchAnyCommonKey,
} from 'utils/immutable'

import {
  FUNCTION,
  QUERY,
  OUTPUT,
  DIGITAL_INPUT,
  ANALOG_INPUT,
  COMPLEX,
  TIMER,
  REFERENCED,
} from 'utils/constants'

import { featureMetaDataSelector, signalsSelector } from './shared'
import { allFunctionsSelector } from './functions'
import { conditionsSelector } from './conditions'
import { queriesSelector } from './queries'
import { getAllFunctionGraphs } from './functionGraphs'
import {
  featureOutputsSelector,
  outputMapsSelector,
  outputPinsSelector,
} from './outputs'

const importedEntitiesSelector = getInOr(Set(), [
  'feature',
  'currentFeature',
  'importedEntities',
])

const EXCLUSIVE_INPUT_TYPES = [DIGITAL_INPUT, ANALOG_INPUT]

const propIsEqual = curry((key, immObjOne, immObjTwo) => {
  return getEq(key, get(key, immObjOne), immObjTwo)
})

const baseSpec = {
  id: equals,
  type: complement(equals),
  mappedOutputId: complement(equals),
}

const startBitIsEqual = propIsEqual('startBit')

const pickConflictPropsToJS = pickToJS([
  'description',
  'connector',
  'location',
  'startBit',
  'numberOfBits',
])

const findOutputConflict = curry((outputsDict, signalsDict, outputId) => {
  const currentOutput = get(outputId, outputsDict)
  const outputCannotConflict = matchAnyCommonKey(baseSpec, currentOutput)
  const startBitEqCurrent = startBitIsEqual(currentOutput)

  return outputsDict.reduce((conflict, output) => {
    if (conflict || outputCannotConflict(output)) {
      return conflict
    }
    const signal = signalsDict.get(currentOutput.get('mappedOutputId'))
    if (getEq('type', 'CAN', output)) {
      if (startBitEqCurrent(output)) {
        const conflictProps = {
          ...pickConflictPropsToJS(currentOutput),
          description: get('description', signal),
        }
        return {
          conflict: conflictProps,
          offender: get('description', output),
        }
      }
      return false
    }

    // For native (SERVICE) outputs the mappedOutputId is a service URI that is
    // not present in the hardware outputPins dictionary, so signal will be null.
    // Fall back to the output node's own properties so the conflict object is
    // still truthy and the CONFLICT badge / disabled checkbox are rendered.
    if (!signal) {
      return {
        conflict: pickConflictPropsToJS(currentOutput),
        offender: get('description', output),
      }
    }

    // output signals are mapped by id not uri
    return {
      conflict: pickConflictPropsToJS(signal),
      offender: get('description', output),
    }
  }, false)
})

const inputBaseSpec = {
  uri: equals,
  subType: complement(equals),
  signal: complement(equals),
  inputConfig: equals,
}

const isExclusiveInputType = compose(
  includes(__, EXCLUSIVE_INPUT_TYPES),
  get('subType')
)

const makeCreateInputConflictObj = curry(
  (queriesDict, conditionsDict, inputSignalsDict, currentCondition) => {
    const currentSignal = get('signal', currentCondition)
    const inputCannotConflict = matchAnyCommonKey(
      inputBaseSpec,
      currentCondition
    )
    if (isExclusiveInputType(currentCondition)) {
      return conditionsDict.reduce((conflict, condition) => {
        if (conflict || inputCannotConflict(condition)) {
          return conflict
        }

        const signalEntity = get(currentSignal, inputSignalsDict)
        const queryDesc = getIn(
          [condition.get('parentQueryUri'), 'description'],
          queriesDict
        )
        const conflictProps = {
          ...pickConflictPropsToJS(signalEntity),
          inputConfig: get('inputConfig', currentCondition),
        }
        return {
          conflict: conflictProps,
          offender: {
            ...conflictProps,
            inputConfig: get('inputConfig', condition),
            label: queryDesc,
          },
        }
      }, false)
    }
    return false
  }
)

const findInputConflict = curry(
  (queriesDict, conditionsDict, inputSignalsDict, queryId) => {
    const createConflictObj = makeCreateInputConflictObj(
      queriesDict,
      conditionsDict,
      inputSignalsDict
    )

    const traverse = (id, visited) => {
      if (visited.has(id)) {
        return false
      }
      const nextVisited = visited.add(id)

      if (conditionsDict.get(id)) {
        const currCond = get(id, conditionsDict)
        return createConflictObj(currCond)
      }

      const conditionUris = queriesDict.getIn([id, 'conditions'])
      if (!conditionUris) {
        return false
      }

      return conditionUris.reduce((accum, uri) => {
        if (accum) {
          return accum
        }

        const currentCondition = get(uri, conditionsDict)
        if (!currentCondition) {
          return accum
        }

        if (get('queryType', currentCondition) === REFERENCED) {
          const refUri = get('refQueryUri', currentCondition)
          if (!refUri) {
            return accum
          }
          return traverse(refUri, nextVisited)
        }

        return createConflictObj(currentCondition)
      }, false)
    }

    return traverse(queryId, Set())
  }
)

const shapeImportQuery = curry(
  (conflictCheckFn, parentUri, { uri, description }) => ({
    type: QUERY,
    description,
    uri,
    parentFunction: parentUri,
    ...conflictCheckFn(uri),
  })
)

const createImportableQueries = curry((conflictCheckFn, parentUri) =>
  compose(map(shapeImportQuery(conflictCheckFn, parentUri)), flatten)
)

const shapeImportOutput = curry(
  (outputsDictionary, conflictCheckFn, key, id) => ({
    type: OUTPUT,
    description: getIn([id, 'description'], outputsDictionary),
    uri: id,
    parentFunction: key,
    ...conflictCheckFn(id),
  })
)

const createImportableOutputs = curry(
  (outputMapsDict, outputsDict, conflictCheckFn, key) =>
    compose(
      map(shapeImportOutput(outputsDict, conflictCheckFn, key)),
      getIn([key, 'outputs'])
    )(outputMapsDict)
)

const makeFuncChildren = reduce(
  (acc, item) => (item.conflict ? acc : concat(acc, [prop('uri', item)])),
  []
)

const funcIsDisabled = all(prop('conflict'))

const mapUris = map(prop('uri'))

const createInventorySet = (functionsArr, childrenArr) =>
  new Set(concat(mapUris(functionsArr), mapUris(childrenArr)))

const createFilterFn = (functionsArr, childrenArr) =>
  compose(not, has(__, createInventorySet(functionsArr, childrenArr)))

const getQueryObj = (queriesDict) => compose(safeToJS, get(__, queriesDict))

const mapOrphanQueries = curry(
  (filterFn, queriesDict, accum, shallowOrphan) => {
    const makeFullJSQuery = getQueryObj(queriesDict)
    const addToAccum = (arg) => (arg ? append(arg, accum) : accum)
    return compose(
      cond([
        [filterFn, compose(addToAccum, makeFullJSQuery)],
        [T, always(accum)],
      ]),
      get('id')
    )(shallowOrphan)
  }
)

const createOrphanQueries = curry((featureMeta, queriesDict, filterFn) =>
  compose(
    reduce(mapOrphanQueries(filterFn, queriesDict), []),
    get('queries')
  )(featureMeta)
)

const getBatchImportFeatureData = createSelector(
  featureMetaDataSelector,
  getAllFunctionGraphs,
  allFunctionsSelector,
  outputMapsSelector,
  featureOutputsSelector,
  queriesSelector,
  conditionsSelector,
  signalsSelector,
  outputPinsSelector,
  importedEntitiesSelector,
  (_, { featureId }) => featureId,
  (
    featureMeta,
    functionGraphs,
    functionsDict,
    outputMapsDict,
    outputsDict,
    queriesDict,
    conditionsDict,
    inputSignalsDict,
    outputPinsDictionary,
    importedEntities,
    featureId
  ) => {
    if (!featureId || !featureMeta || !functionGraphs) {
      return {}
    }

    const isOutputConflict = findOutputConflict(
      outputsDict,
      outputPinsDictionary
    )
    const isQueryConflict = findInputConflict(
      queriesDict,
      conditionsDict,
      inputSignalsDict
    )

    const makeOrphanQueries = createOrphanQueries(featureMeta, queriesDict)

    const makeImportableQueries = createImportableQueries(isQueryConflict)
    const makeImportableOutputs = createImportableOutputs(
      outputMapsDict,
      outputsDict,
      isOutputConflict
    )

    // Collect direct output query URIs for every donor function. A COMPLEX/TIMER
    // query that is in a function graph but NOT a direct output query is a
    // "carried orphan" — it is referenced via a REFERENCED condition inside
    // another Logic Set and must show the ORPHAN badge.
    const donorDirectOutputQueryUris = new Set(
      Object.keys(functionGraphs)
        .map((fnUri) => outputMapsDict.getIn([fnUri, 'outputQuery']))
        .filter(Boolean)
    )

    const { functions, children } = Object.entries(functionGraphs).reduce(
      (accum, [key, value]) => {
        // `key` here is the function uri
        // `value` here is an array of arrays with query uris in them
        const initialQueries = makeImportableQueries(key)(value)
        const initialOutputs = makeImportableOutputs(key)

        const outputFunctionChildren = makeFuncChildren(initialOutputs)

        const outputs = initialOutputs.map((output) => {
          return {
            ...output,
            siblings: outputFunctionChildren.filter((id) => id !== output.uri),
          }
        })

        // Preserve parentBox from the function graph nodes before shapeImportQuery
        // strips them down to { uri, description }. The graph traversal (makeNode)
        // sets parentBox to the carrying Logic Set's URI — the raw API query field
        // may be null, so the graph node is the authoritative source.
        const graphNodes = flatten(value)

        const queries = initialQueries.map((query) => {
          const queryData = queriesDict.get(query.uri)
          const queryType = queryData ? queryData.get('queryType') : null
          const graphNode = graphNodes.find((node) => node.uri === query.uri)
          const carriedByUri = graphNode ? graphNode.parentBox : null
          // A query with a parentBox is carried by another Logic Set and is
          // properly connected — it must NOT be shown as an orphan.
          const isCarriedOrphan =
            queryData &&
            (queryType === COMPLEX || queryType === TIMER) &&
            !donorDirectOutputQueryUris.has(query.uri) &&
            !carriedByUri

          if (isCarriedOrphan) {
            const carriedBy = carriedByUri
              ? getIn([carriedByUri, 'description'], queriesDict)
              : null

            // If this carried orphan has no detected conflict of its own,
            // inherit from any conflicting direct-output-query sibling in the
            // same function graph. Carried orphans and output queries are always
            // imported together as siblings, so a DI conflict on the output
            // query applies equally to its carried orphans.
            const inheritedConflict =
              !query.conflict &&
              initialQueries.find(
                (q) => q.conflict && donorDirectOutputQueryUris.has(q.uri)
              )

            return {
              ...query,
              ...(inheritedConflict
                ? {
                    conflict: inheritedConflict.conflict,
                    offender: inheritedConflict.offender,
                  }
                : {}),
              orphan: true,
              carriedBy,
              siblings: initialQueries.map(prop('uri')),
            }
          }

          return {
            ...query,
            siblings: initialQueries.map(prop('uri')),
          }
        })

        const functionChildren = [...outputs, ...queries]
        const func = {
          type: FUNCTION,
          description: getIn([key, 'description'], functionsDict),
          uri: key,
          children: functionChildren,
          disabled: funcIsDisabled(functionChildren),
          dependents: {
            outputs: outputFunctionChildren,
            queries: makeFuncChildren(queries),
          },
        }

        return {
          functions: [...accum.functions, func],
          children: [...accum.children, ...functionChildren],
        }
      },
      {
        functions: [],
        children: [],
      }
    )

    const orphanQueryFilter = createFilterFn(functions, children)

    const orphanQueries = makeOrphanQueries(orphanQueryFilter)

    // featureMeta.queries only contains SIMPLE query refs from the API.
    // COMPLEX and TIMER queries (Logic Sets / Timers) owned by the donor
    // feature live in queriesDict but are never iterated by makeOrphanQueries.
    // Detect them separately: any COMPLEX/TIMER query that belongs to the
    // donor feature (present in importedEntities) and is not already covered
    // by a function graph node is an orphan.
    const complexTimerOrphanQueries = queriesDict.reduce((acc, query, uri) => {
      const queryType = query.get('queryType')
      if (
        importedEntities.has(uri) &&
        orphanQueryFilter(uri) &&
        (queryType === COMPLEX || queryType === TIMER) &&
        !orphanQueries.some((q) => q.uri === uri)
      ) {
        return [...acc, safeToJS(query)]
      }
      return acc
    }, [])

    const allOrphanQueries = [...orphanQueries, ...complexTimerOrphanQueries]

    const initialImportableOrphanQueries = makeImportableQueries(
      '/functions/placeholder'
    )(allOrphanQueries)

    const importableOrphanQueries = initialImportableOrphanQueries.map(
      (query) => {
        const originalQuery = allOrphanQueries.find((q) => q.uri === query.uri)
        const carriedByUri = originalQuery && originalQuery.parentBox
        const carriedBy = carriedByUri
          ? getIn([carriedByUri, 'description'], queriesDict)
          : null
        return {
          ...query,
          orphan: true,
          carriedBy,
          siblings: [query.uri],
        }
      }
    )

    const data = [
      ...functions,

      ...(importableOrphanQueries.length > 0
        ? [
            {
              type: '',
              description: 'Orphan Queries',
              uri: '/functions/placeholder',
              children: importableOrphanQueries,
              dependents: {
                outputs: [],
                queries: mapUris(importableOrphanQueries),
              },
            },
          ]
        : []),
    ]

    return {
      data,
      featureDescription: featureMeta.get('description'),
    }
  }
)

export { getBatchImportFeatureData }
